/**
 * The campaign contract — bata-reqs issue 33 ("Wire contract, phase 1", "Wire contract, phase 2",
 * their corrections and the as-built notes), built by bata-platform PR 585 (ADR 6064, the response
 * fit and its backtest), PR 586 (ADR 6065, the advisory campaign simulation) and PR 587 (ADR 6066,
 * the article-level terms), then PR 588 (ADR 6067, in-scope cannibalisation), PR 589 (ADR 6068, the
 * stock test and the service verdict) and PR 590 (ADR 6069, markdown exposure, which realised margin
 * now includes). bata-ui `docs/adr/6017` (the three campaign pages and the response fit on
 * the Promotions › By mechanic page).
 *
 * ══ PINNED 2026-10-07 ═════════════════════════════════════════════════════════
 *
 * The client was re-pinned to bata-platform main at `17950e3`, which publishes all nine routes
 * (PRs 585–590, and 592's documented 400 on the cursor-paged reads). Every `…_READ` / `…_WRITE`
 * below is now the generated operation — the operationIds assumed from FastAPI's convention were
 * all exact — so the pages issue their requests. Every response `…Shape` is the generated model
 * re-exported under the name the readers already used. What the generated contract says
 * differently from the hand-typed shapes, absorbed here and nowhere else:
 *   - the `CursorPage` envelope's `has_more` and `next_cursor` are optional (FastAPI defaults), so
 *     {@link CursorPageShape} says so and the reader takes an absent `has_more` as no more;
 *   - a fit's `validation.gate` is the closed enum, and the results' `refusal` is always present
 *     (`null` when the run answered) — both narrower, so nothing in a reader changes;
 *   - the request's `banner`, `comparator`, `stores` and `articleScope` are optional on the wire
 *     (server defaults). The request shapes below stay hand-typed and stricter — Compose always
 *     states all four — and are assignable to the generated request (the typecheck is the proof);
 *   - every query member of the generated params is optional; the params builders keep their
 *     required members (G16) and the `…Read` types take the generated params.
 * What follows is the text as it stood before the pin.
 *
 * bata-platform merged the routes, but `src/api/spec/openapi.json` is still pinned to contract
 * 0.71.0, which does not publish them. So — exactly as `promotions.ts` and `service-tradeoff.ts` did
 * before their re-pins (ADR 9037's "built before its read lands") — the shapes below are typed by
 * hand from the router as merged (`services/api/routers/inseason_campaigns.py`, aefecc8), every
 * `…_READ` / `…_WRITE` constant is `null`, and every page draws its designed «In arrivo» state and
 * issues NO request to these routes. The `…_PROPOSED_OPERATION` constants are the operations as the
 * router declares them; the tests mount each page over them with a stand-in `fetch`.
 *
 * `src/features/inseason/campaigns/campaigns.test.tsx` keeps the tripwire: the pinned spec has each
 * path exactly when its constant is wired. The re-pin (1) re-exports the generated shapes under the
 * names below, (2) sets each constant to the generated operation, (3) keeps the readers — the pages
 * read ONLY what they build here. **Every field access of the platform's shapes is in this file.**
 *
 *   GET  /api/v1/inseason/campaigns/response?country_key=[&fit_id=]       the market's newest fit
 *   GET  /api/v1/inseason/campaigns/response/fits?country_key=            the fits, newest first
 *   GET  /api/v1/inseason/campaigns/response/backtest?fit_id=&method=     the held-out windows
 *   POST /api/v1/inseason/campaigns/simulations                           ask a campaign simulation
 *   GET  /api/v1/inseason/campaigns/simulations?run_id=                   one base run's, newest first
 *   GET  /api/v1/inseason/campaigns/simulations/{simulation_id}           one, and its run's state
 *   GET  /api/v1/inseason/campaigns/simulations/{simulation_id}/results   its answer (409 until done)
 *   GET  /api/v1/inseason/campaigns/scope/options?run_id=                 what Compose may offer
 *   GET  /api/v1/inseason/campaigns/scope?run_id=&starts_on=&channel=…    the resolved count
 *
 * Conventions (the contract's): query parameters snake_case, bodies camelCase, list reads the
 * platform's `CursorPage` (`items`, `next_cursor`, `has_more`, `page_size` — snake_case, the phase 2
 * correction of phase 1's `nextCursor`). An uplift is a ratio less one; every interval is 80%. Money
 * is a JSON number in `currencyCode`'s currency, never converted. A figure with no value is `null`
 * with an `absentReason`, never a zero.
 */

import {
  listCampaignResponseBacktest, listCampaignResponseFits, listCampaignSimulations, readCampaignResponse, readCampaignResults,
  readCampaignScope, readCampaignScopeArticles, readCampaignScopeOptions, readCampaignSimulation, requestCampaignSimulation,
  type ListCampaignResponseBacktestParams, type ListCampaignResponseFitsParams, type ListCampaignSimulationsParams, type OperationSpec,
  type ReadCampaignResponseParams, type ReadCampaignScopeArticlesParams, type ReadCampaignScopeOptionsParams, type ReadCampaignScopeParams,
  type RequestCampaignSimulationParams,
} from '../generated/operations';
import type {
  CampaignAlternativeView, CampaignMeasureView, CampaignResponseView, CampaignResultsView, CampaignSimulationView, ChannelView,
  ClusterEffectView, ServicesApiRoutersInseasonCampaignsClusterView, CoverageView, FitSummaryView, GroupEffectView, HoldoutView, MechanicView, PointView, RateView,
  RefusalView, ScopeArticlesView, ScopeCountView, ScopeOptionsView, UpliftView, ValidationView, AlternativeResultView, DifferenceView,
} from '../generated/types';

// ── the vocabularies ─────────────────────────────────────────────────────────

/**
 * The mechanics this build can name — bata-platform ADR 8011's ceiling, in its declared order (the
 * order the pages list them in, never a ranking). Which of them may be simulated is the
 * `promotion.mechanic_vocabulary` registry row in force ({@link governedMechanics}).
 */
export const CAMPAIGN_MECHANICS = ['multi_buy', 'second_item_percent_off', 'article_percent_off'] as const;
export type CampaignMechanic = (typeof CAMPAIGN_MECHANICS)[number];

/** How a mechanic's parameter is written: one depth, or a buy/pay pair ("3x2"). */
export const PARAMETER_KINDS = ['percent_off', 'buy_pay_quantities'] as const;
export type ParameterKind = (typeof PARAMETER_KINDS)[number];

/** Each ceiling code's parameter (ADR 8011): what Compose asks for before a fit has said so. */
export const MECHANIC_PARAMETER: Readonly<Record<CampaignMechanic, ParameterKind>> = {
  multi_buy: 'buy_pay_quantities',
  second_item_percent_off: 'percent_off',
  article_percent_off: 'percent_off',
};

/**
 * The mechanics a campaign never simulates: `article_percent_off` IS the Zona sale markdown
 * (bata-platform ADR 8011), and the Zona sale and the multi-buy campaigns are mutually exclusive —
 * no discount on a discount (bata-reqs issue 35 client rule C4, bata-platform ADR 6078). The
 * vocabulary row keeps it (the response fit reads the history's mix with it); Compose never offers it
 * and the platform refuses it (422 `mechanic_is_markdown`).
 */
export const MARKDOWN_MECHANICS: readonly CampaignMechanic[] = ['article_percent_off'];

/** The registry row that governs which mechanics exist (estate row, read on today's date). */
export const MECHANIC_VOCABULARY_KEY = 'promotion.mechanic_vocabulary';

/** The store channels a campaign can run in, and the request's "both". */
export const STORE_CHANNELS = ['OWN_STORE', 'OUTLET'] as const;
export const CAMPAIGN_CHANNELS = ['OWN_STORE', 'OUTLET', 'BOTH'] as const;
export type CampaignChannel = (typeof CAMPAIGN_CHANNELS)[number];

/** The banners the request types; only `BOTH` is answerable until a door banner is governed. */
export const CAMPAIGN_BANNERS = ['BOTH', 'BATA', 'BG'] as const;
export type CampaignBanner = (typeof CAMPAIGN_BANNERS)[number];
export const ANSWERABLE_BANNER: CampaignBanner = 'BOTH';

/**
 * The doors: every door of the channel, climate-zone clusters, commercial districts (the store
 * workbook's `Distric`, bata-platform ADR 6076), or a list.
 */
export const STORES_KINDS = ['all', 'clusters', 'districts', 'doors'] as const;
export type StoresKind = (typeof STORES_KINDS)[number];

/**
 * The include/exclude classes the request accepts today (D2). `SALE_ZONE` and `BRAND_SOURCE` are
 * refused with `scope_class_unavailable` until their sources land (BRAND_SOURCE waits for ch00's
 * source brand label); the refusal names the class, and {@link LATER_SCOPE_CLASSES} has its words.
 */
export const SCOPE_CLASSES = ['CATEGORY', 'SUB_CATEGORY'] as const;
export type ScopeClass = (typeof SCOPE_CLASSES)[number];
/**
 * An article named by its code (bata-platform ADR 6076): the planner's own list, matched by key,
 * checked against the article master in force (`/scope/articles`), refused 422 `article_unknown`
 * when the master does not carry it. Not offered in a row's class select: it has its own list.
 */
export const ARTICLE_SCOPE_CLASS = 'ARTICLE';
export type ArticleScopeKind = ScopeClass | typeof ARTICLE_SCOPE_CLASS;
export const LATER_SCOPE_CLASSES = ['SALE_ZONE', 'BRAND_SOURCE'] as const;

/**
 * One to six alternatives (owner ruling 2026-10-09; bata-platform ADR 6076), every one a governed
 * mechanic — never "no promotion" (D5). One alternative is answered alone, against the history's
 * promotion mix; two or more are also differenced against the comparator.
 */
export const MIN_ALTERNATIVES = 1;
export const MAX_ALTERNATIVES = 6;
/** The request's bound on an alternative's label. */
export const MAX_LABEL_LENGTH = 128;

/** A fit's backtest gate per mechanic × channel; anything but `open` is "not yet validated". */
export const GATES = ['open', 'closed', 'not_evaluable'] as const;
export type Gate = (typeof GATES)[number];

/** A simulation's measures, in the order the pages draw them. */
export const CAMPAIGN_MEASURES = [
  'units', 'incremental_units', 'net_sales', 'discount_cost', 'gross_margin', 'realised_margin', 'pull_forward_units',
  'pull_forward_net_sales', 'in_scope_cannibalised_units', 'in_scope_cannibalised_net_sales', 'excluded_net_sales_change',
  'terminal_units_change', 'terminal_write_down_change', 'markdown_cost_change',
  'service_level', 'door_options_breaching', 'units_at_risk',
] as const;
export type CampaignMeasure = (typeof CAMPAIGN_MEASURES)[number];

/** The measures in money (`currencyCode` set); absent together until the article panel lands. */
export const MONEY_MEASURES: ReadonlySet<CampaignMeasure> = new Set([
  'net_sales', 'discount_cost', 'gross_margin', 'realised_margin', 'pull_forward_net_sales', 'excluded_net_sales_change',
  'in_scope_cannibalised_net_sales', 'terminal_write_down_change', 'markdown_cost_change',
]);

/**
 * The stock test's measures (ADR 6068), each alternative's own and never differenced: the service
 * level (a share), the door × article pairs below target, and the expected uncovered units.
 */
export const SERVICE_MEASURES: ReadonlySet<CampaignMeasure> = new Set(['service_level', 'door_options_breaching', 'units_at_risk']);

/** Markdown exposure (ADR 6069): the tail realised margin now includes. */
export const MARKDOWN_MEASURES: ReadonlySet<CampaignMeasure> = new Set(['terminal_units_change', 'terminal_write_down_change', 'markdown_cost_change']);

/** In-scope cannibalisation and the volume new to the base (ADR 6067). */
export const CANNIBALISATION_MEASURES: ReadonlySet<CampaignMeasure> = new Set(['in_scope_cannibalised_units', 'in_scope_cannibalised_net_sales', 'incremental_units']);

/** A verdict against `inseason.pull.service_target` — a constraint (D4), never a ranking. */
export const SERVICE_VERDICTS = ['pass', 'breach'] as const;
export type ServiceVerdict = (typeof SERVICE_VERDICTS)[number];

/**
 * The one measure alternatives are compared on (D4); units and net sales are shown, never compared.
 * Since ADR 6069 it covers the window, the pull-forward and the markdown exposure (the window plus
 * pull-forward where the exposure is absent) — a change of meaning with the shape unchanged.
 */
export const COMPARED_MEASURE: CampaignMeasure = 'realised_margin';

/** Why a figure is absent (ADR 6065, 6066). A word outside this list is shown verbatim. */
export const ABSENT_REASONS = [
  'panel_not_landed', 'no_sales_in_lookback', 'no_cost_in_lookback', 'objective_config_absent', 'discount_rate_not_fitted',
  'currencies_differ', 'measure_not_fitted', 'excluded_effect_not_fitted', 'cannibalisation_not_fitted', 'price_path_absent',
  'price_run_absent', 'service_target_absent', 'stock_position_absent', 'forecast_levels_absent',
] as const;
export type AbsentReason = (typeof ABSENT_REASONS)[number];

/** Why one alternative is refused (ADR 6064 `response.py`). */
export const ALTERNATIVE_REFUSALS = [
  'too_few_windows', 'depth_outside_support', 'depth_required', 'quantities_outside_support', 'parameter_unidentified',
  'mechanic_not_fitted', 'interval_too_wide', 'mechanic_is_markdown',
] as const;
export type AlternativeRefusal = (typeof ALTERNATIVE_REFUSALS)[number];

/** Why a whole run answered nothing (`CampaignResultsView.refusal`, the phase 2 as-built note). */
export const RUN_REFUSALS = [
  'planning.parameters.missing', 'planning.campaign.base_forecast_unavailable', 'planning.campaign.base_forecast_horizon',
  'planning.campaign.scope_empty', 'planning.campaign.response_fit_absent', 'planning.campaign.not_a_campaign_simulation',
  'planning.campaign.lifecycle_phase_feed_absent',
] as const;
export type RunRefusal = (typeof RUN_REFUSALS)[number];

/** What the request refuses (422), each with the members it names. */
export const REQUEST_REFUSALS = [
  'planning.inseason.campaign.too_few_alternatives', 'planning.inseason.campaign.too_many_alternatives',
  'planning.inseason.campaign.duplicate_alternative', 'planning.inseason.campaign.window_invalid',
  'planning.inseason.campaign.mechanic_not_governed', 'planning.inseason.campaign.parameter_invalid',
  'planning.inseason.campaign.scope_class_unavailable', 'planning.inseason.campaign.banner_unresolved',
  'planning.inseason.campaign.comparator_invalid', 'planning.inseason.campaign.parameters_missing',
  'planning.inseason.campaign.base_forecast_unavailable', 'planning.inseason.campaign.response_not_fitted',
  'planning.inseason.campaign.stores_invalid', 'planning.inseason.campaign.article_unknown',
  'planning.inseason.campaign.mechanic_is_markdown', 'planning.inseason.campaign.article_in_markdown_phase',
] as const;
export type RequestRefusal = (typeof REQUEST_REFUSALS)[number];

/**
 * Why the lifecycle gate keeps an article out of a campaign (bata-platform ADR 6078): its phase in
 * force is the Zona sale or the outlet in a month of the window. An article with no phase stated is
 * NOT excluded — it is kept as main line and counted apart (owner ruling 2026-10-10).
 */
export const PHASE_EXCLUSION_REASONS = ['phase_zona_sales', 'phase_outlet'] as const;
export type PhaseExclusionReason = (typeof PHASE_EXCLUSION_REASONS)[number];
/** The two reasons the request refuses an article named in the list for (422 `article_in_markdown_phase`). */
export const MARKDOWN_PHASE_REASONS: readonly PhaseExclusionReason[] = ['phase_zona_sales', 'phase_outlet'];
/** Who stated the phase: a planner's decision, bata-data's inference, or nobody. */
export const PHASE_SOURCES = ['decision', 'inferred', 'none'] as const;
export type PhaseSource = (typeof PHASE_SOURCES)[number];

/** 404 on the response read: no fit for the market yet — the designed state, never an error. */
export const RESPONSE_NOT_FITTED = 'planning.inseason.campaign.response_not_fitted';
/** 503: the platform registered the routes and wired no reader — «In arrivo». */
export const CAMPAIGNS_UNAVAILABLE = 'planning.inseason.campaign.unavailable';
/** 409 on the results read until the run has succeeded. */
export const RESULTS_NOT_COMPLETE = 'planning.inseason.simulation.not_complete';

/** The comparison every figure rests on (bata-data ADR 8007; ADR 6065). */
export const RESPONSE_COMPARISON = 'neighbour_days_same_doors';
export const CAMPAIGN_COMPARISON = 'between_alternatives';

// ── wire shapes: the response fit (phase 1, phase 3 additions) ───────────────

export type UpliftShape = UpliftView;

export type RateShape = RateView;

export type PointShape = PointView;

export type RefusalShape = RefusalView;

export type CoverageShape = CoverageView;

export type ValidationShape = ValidationView;

export type EffectShape = ClusterEffectView;

// The platform names two cluster views since the run-scope reads (bata-platform ADR 9036), so the
// generator qualifies both by their router; the campaigns' is this one.
export type ClusterShape = ServicesApiRoutersInseasonCampaignsClusterView;

export type GroupEffectShape = GroupEffectView;

export type MechanicShape = MechanicView;

export type ChannelShape = ChannelView;

export type CampaignResponseShape = CampaignResponseView;

/** The platform's `CursorPage`; `has_more` and `next_cursor` are optional on the wire (defaults). */
export interface CursorPageShape<T> {
  readonly items: readonly T[];
  readonly next_cursor?: string | null;
  readonly has_more?: boolean;
  readonly page_size: number;
}

export type FitSummaryShape = FitSummaryView;

export type HoldoutShape = HoldoutView;

// ── wire shapes: the campaign simulation (phase 2, phase 3 additions) ────────

export type StoresShape =
  | { readonly kind: 'all' }
  | { readonly kind: 'clusters'; readonly clusterKeys: readonly string[] }
  | { readonly kind: 'districts'; readonly districtKeys: readonly string[] }
  | { readonly kind: 'doors'; readonly storeKeys: readonly string[] };

export interface ScopeRowShape {
  readonly kind: string;
  readonly value: string;
  /**
   * A `SUB_CATEGORY` row's category (bata-platform ADR 6071, additive): a sub-category name repeats
   * under several categories, and an unqualified one that does is refused (422
   * `planning.inseason.campaign.sub_category_ambiguous`). Sent whenever the options read named it.
   */
  readonly category?: string | null;
}

export interface ArticleScopeShape {
  readonly include: readonly ScopeRowShape[];
  readonly exclude: readonly ScopeRowShape[];
}

export interface AlternativeRequestShape {
  readonly label: string;
  readonly mechanicCode: string;
  readonly depthPct: number | null;
  readonly buyQuantity: number | null;
  readonly payQuantity: number | null;
}

export interface CampaignSimulationRequestShape {
  readonly baseRunId: string;
  readonly scenarioId: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly channel: CampaignChannel;
  readonly banner: CampaignBanner;
  readonly stores: StoresShape;
  readonly articleScope: ArticleScopeShape;
  readonly alternatives: readonly AlternativeRequestShape[];
  readonly comparator: number;
}

export type CampaignAlternativeShape = CampaignAlternativeView;

export type CampaignSimulationShape = CampaignSimulationView;

export type CampaignMeasureShape = CampaignMeasureView;

export type AlternativeResultShape = AlternativeResultView;

export type DifferenceShape = DifferenceView;

export type CampaignResultsShape = CampaignResultsView;

export type ScopeOptionsShape = ScopeOptionsView;

export type ScopeCountShape = ScopeCountView;

export type ScopeArticlesShape = ScopeArticlesView;

// ── the operations ───────────────────────────────────────────────────────────

const PREFIX = '/api/v1/inseason/campaigns';

export interface CampaignResponseParams {
  readonly query: { readonly country_key: string; readonly fit_id?: string };
}
export type CampaignResponseRead = OperationSpec<ReadCampaignResponseParams, CampaignResponseShape>;
export const CAMPAIGN_RESPONSE_PATH_IN_SPEC = `${PREFIX}/response`;
export const CAMPAIGN_RESPONSE_PROPOSED_OPERATION: CampaignResponseRead = {
  operationId: 'read_campaign_response_api_v1_inseason_campaigns_response_get',
  method: 'GET',
  path: CAMPAIGN_RESPONSE_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: [],
  queryParams: ['country_key', 'fit_id'],
  hasBody: false,
};
/** The generated operation (pinned 2026-10-07); `null` would draw «In arrivo» and request nothing. */
export const CAMPAIGN_RESPONSE_READ: CampaignResponseRead | null = readCampaignResponse;

export interface CampaignFitsParams {
  readonly query: { readonly country_key: string; readonly limit?: number; readonly cursor?: string };
}
export type CampaignFitsRead = OperationSpec<ListCampaignResponseFitsParams, CursorPageShape<FitSummaryShape>>;
export const CAMPAIGN_FITS_PATH_IN_SPEC = `${PREFIX}/response/fits`;
export const CAMPAIGN_FITS_PROPOSED_OPERATION: CampaignFitsRead = {
  operationId: 'list_campaign_response_fits_api_v1_inseason_campaigns_response_fits_get',
  method: 'GET',
  path: CAMPAIGN_FITS_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: [],
  queryParams: ['limit', 'cursor', 'country_key'],
  hasBody: false,
};
/** Not drawn by a page today; wired with the others at the 2026-10-07 re-pin. */
export const CAMPAIGN_FITS_READ: CampaignFitsRead | null = listCampaignResponseFits;

export interface CampaignBacktestParams {
  readonly query: {
    readonly fit_id: string;
    readonly method: 'rolling_origin' | 'leave_one_out';
    readonly channel_key?: string;
    readonly mechanic_code?: string;
    readonly limit?: number;
    readonly cursor?: string;
  };
}
export type CampaignBacktestRead = OperationSpec<ListCampaignResponseBacktestParams, CursorPageShape<HoldoutShape>>;
export const CAMPAIGN_BACKTEST_PATH_IN_SPEC = `${PREFIX}/response/backtest`;
export const CAMPAIGN_BACKTEST_PROPOSED_OPERATION: CampaignBacktestRead = {
  operationId: 'list_campaign_response_backtest_api_v1_inseason_campaigns_response_backtest_get',
  method: 'GET',
  path: CAMPAIGN_BACKTEST_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: [],
  queryParams: ['limit', 'cursor', 'fit_id', 'method', 'channel_key', 'mechanic_code'],
  hasBody: false,
};
/** Not drawn by a page today (the fit's own coverage counts are); wired at the 2026-10-07 re-pin. */
export const CAMPAIGN_BACKTEST_READ: CampaignBacktestRead | null = listCampaignResponseBacktest;

export interface CampaignRequestParams {
  readonly body: CampaignSimulationRequestShape;
}
export type CampaignRequestWrite = OperationSpec<RequestCampaignSimulationParams, CampaignSimulationShape>;
export const CAMPAIGN_SIMULATIONS_PATH_IN_SPEC = `${PREFIX}/simulations`;
export const CAMPAIGN_REQUEST_PROPOSED_OPERATION: CampaignRequestWrite = {
  operationId: 'request_campaign_simulation_api_v1_inseason_campaigns_simulations_post',
  method: 'POST',
  path: CAMPAIGN_SIMULATIONS_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: [],
  queryParams: [],
  hasBody: true,
};
export const CAMPAIGN_REQUEST_WRITE: CampaignRequestWrite | null = requestCampaignSimulation;

export interface CampaignSimulationsParams {
  readonly query: { readonly run_id: string; readonly limit?: number; readonly cursor?: string };
}
export type CampaignSimulationsRead = OperationSpec<ListCampaignSimulationsParams, CursorPageShape<CampaignSimulationShape>>;
export const CAMPAIGN_SIMULATIONS_PROPOSED_OPERATION: CampaignSimulationsRead = {
  operationId: 'list_campaign_simulations_api_v1_inseason_campaigns_simulations_get',
  method: 'GET',
  path: CAMPAIGN_SIMULATIONS_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: [],
  queryParams: ['limit', 'cursor', 'run_id'],
  hasBody: false,
};
export const CAMPAIGN_SIMULATIONS_READ: CampaignSimulationsRead | null = listCampaignSimulations;

export interface CampaignSimulationParams {
  readonly path: { readonly simulation_id: string };
}
export type CampaignSimulationRead = OperationSpec<CampaignSimulationParams, CampaignSimulationShape>;
export const CAMPAIGN_SIMULATION_PATH_IN_SPEC = `${PREFIX}/simulations/{simulation_id}`;
export const CAMPAIGN_SIMULATION_PROPOSED_OPERATION: CampaignSimulationRead = {
  operationId: 'read_campaign_simulation_api_v1_inseason_campaigns_simulations__simulation_id__get',
  method: 'GET',
  path: CAMPAIGN_SIMULATION_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: ['simulation_id'],
  queryParams: [],
  hasBody: false,
};
export const CAMPAIGN_SIMULATION_READ: CampaignSimulationRead | null = readCampaignSimulation;

export type CampaignResultsRead = OperationSpec<CampaignSimulationParams, CampaignResultsShape>;
export const CAMPAIGN_RESULTS_PATH_IN_SPEC = `${PREFIX}/simulations/{simulation_id}/results`;
export const CAMPAIGN_RESULTS_PROPOSED_OPERATION: CampaignResultsRead = {
  operationId: 'read_campaign_results_api_v1_inseason_campaigns_simulations__simulation_id__results_get',
  method: 'GET',
  path: CAMPAIGN_RESULTS_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: ['simulation_id'],
  queryParams: [],
  hasBody: false,
};
export const CAMPAIGN_RESULTS_READ: CampaignResultsRead | null = readCampaignResults;

export interface CampaignScopeOptionsParams {
  readonly query: { readonly run_id: string };
}
export type CampaignScopeOptionsRead = OperationSpec<ReadCampaignScopeOptionsParams, ScopeOptionsShape>;
export const CAMPAIGN_SCOPE_OPTIONS_PATH_IN_SPEC = `${PREFIX}/scope/options`;
export const CAMPAIGN_SCOPE_OPTIONS_PROPOSED_OPERATION: CampaignScopeOptionsRead = {
  operationId: 'read_campaign_scope_options_api_v1_inseason_campaigns_scope_options_get',
  method: 'GET',
  path: CAMPAIGN_SCOPE_OPTIONS_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: [],
  queryParams: ['run_id'],
  hasBody: false,
};
export const CAMPAIGN_SCOPE_OPTIONS_READ: CampaignScopeOptionsRead | null = readCampaignScopeOptions;

export interface CampaignScopeCountParams {
  readonly query: {
    readonly run_id: string;
    readonly starts_on: string;
    readonly channel: CampaignChannel;
    readonly stores_kind: StoresKind;
    readonly cluster_key?: readonly string[];
    readonly district_key?: readonly string[];
    readonly store_key?: readonly string[];
    readonly include_category?: readonly string[];
    readonly include_sub_category?: readonly string[];
    readonly exclude_category?: readonly string[];
    readonly exclude_sub_category?: readonly string[];
    readonly include_article?: readonly string[];
    readonly exclude_article?: readonly string[];
    /** The window's last day (ADR 6078): the lifecycle gate reads every month it touches. */
    readonly ends_on?: string;
  };
}
export type CampaignScopeCountRead = OperationSpec<ReadCampaignScopeParams, ScopeCountShape>;
export const CAMPAIGN_SCOPE_COUNT_PATH_IN_SPEC = `${PREFIX}/scope`;
export const CAMPAIGN_SCOPE_COUNT_PROPOSED_OPERATION: CampaignScopeCountRead = {
  operationId: 'read_campaign_scope_api_v1_inseason_campaigns_scope_get',
  method: 'GET',
  path: CAMPAIGN_SCOPE_COUNT_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: [],
  queryParams: [
    'run_id', 'starts_on', 'channel', 'stores_kind', 'cluster_key', 'district_key', 'store_key', 'include_category',
    'include_sub_category', 'exclude_category', 'exclude_sub_category', 'include_article', 'exclude_article', 'ends_on',
  ],
  hasBody: false,
};
export const CAMPAIGN_SCOPE_COUNT_READ: CampaignScopeCountRead | null = readCampaignScope;

/**
 * A typed article list checked against the article master in force (bata-platform ADR 6076): the
 * known with their name, labels and codes, the unknown named. Writes nothing; at most
 * {@link MAX_ARTICLES_PER_LOOKUP} keys a read.
 */
export interface CampaignScopeArticlesParams {
  readonly query: {
    readonly run_id: string;
    readonly article_key: readonly string[];
    /** The window (ADR 6078): each article's lifecycle exclusion over it; this month without it. */
    readonly starts_on?: string;
    readonly ends_on?: string;
  };
}
export type CampaignScopeArticlesRead = OperationSpec<ReadCampaignScopeArticlesParams, ScopeArticlesShape>;
export const CAMPAIGN_SCOPE_ARTICLES_PATH_IN_SPEC = `${PREFIX}/scope/articles`;
export const CAMPAIGN_SCOPE_ARTICLES_PROPOSED_OPERATION: CampaignScopeArticlesRead = {
  operationId: 'read_campaign_scope_articles_api_v1_inseason_campaigns_scope_articles_get',
  method: 'GET',
  path: CAMPAIGN_SCOPE_ARTICLES_PATH_IN_SPEC,
  tag: 'inseason',
  pathParams: [],
  queryParams: ['run_id', 'article_key', 'starts_on', 'ends_on'],
  hasBody: false,
};
export const CAMPAIGN_SCOPE_ARTICLES_READ: CampaignScopeArticlesRead | null = readCampaignScopeArticles;
/** The read's own bound on one call (the route's `max_length`). */
export const MAX_ARTICLES_PER_LOOKUP = 500;
/** How many article rows the request takes on one side (the request's `max_length`). */
export const MAX_ARTICLE_ROWS = 2000;

/** Every campaign operation the pages use, wired or not — one place for the pages to hand down. */
export interface CampaignOperations {
  readonly response: CampaignResponseRead | null;
  readonly request: CampaignRequestWrite | null;
  readonly simulations: CampaignSimulationsRead | null;
  readonly simulation: CampaignSimulationRead | null;
  readonly results: CampaignResultsRead | null;
  readonly scopeOptions: CampaignScopeOptionsRead | null;
  readonly scopeCount: CampaignScopeCountRead | null;
  readonly scopeArticles: CampaignScopeArticlesRead | null;
}

/** As pinned: every one the generated operation since the 2026-10-07 re-pin. */
export const CAMPAIGN_OPERATIONS: CampaignOperations = {
  response: CAMPAIGN_RESPONSE_READ,
  request: CAMPAIGN_REQUEST_WRITE,
  simulations: CAMPAIGN_SIMULATIONS_READ,
  simulation: CAMPAIGN_SIMULATION_READ,
  results: CAMPAIGN_RESULTS_READ,
  scopeOptions: CAMPAIGN_SCOPE_OPTIONS_READ,
  scopeCount: CAMPAIGN_SCOPE_COUNT_READ,
  scopeArticles: CAMPAIGN_SCOPE_ARTICLES_READ,
};

/** As the router declares them — what the tests mount the pages over. */
export const CAMPAIGN_PROPOSED_OPERATIONS: CampaignOperations = {
  response: CAMPAIGN_RESPONSE_PROPOSED_OPERATION,
  request: CAMPAIGN_REQUEST_PROPOSED_OPERATION,
  simulations: CAMPAIGN_SIMULATIONS_PROPOSED_OPERATION,
  simulation: CAMPAIGN_SIMULATION_PROPOSED_OPERATION,
  results: CAMPAIGN_RESULTS_PROPOSED_OPERATION,
  scopeOptions: CAMPAIGN_SCOPE_OPTIONS_PROPOSED_OPERATION,
  scopeCount: CAMPAIGN_SCOPE_COUNT_PROPOSED_OPERATION,
  scopeArticles: CAMPAIGN_SCOPE_ARTICLES_PROPOSED_OPERATION,
};

// ── the pages' reading: shared pieces ────────────────────────────────────────

/** A mechanic as the pages say it: a code this build names, else the platform's code verbatim. */
export interface MechanicRef {
  readonly code: CampaignMechanic | null;
  readonly raw: string;
}

/** An estimate with its interval; `low`/`high` both present or both `null`. */
export interface Interval {
  readonly estimate: number;
  readonly low: number | null;
  readonly high: number | null;
}

const isMechanic = (value: string | null | undefined): value is CampaignMechanic => (CAMPAIGN_MECHANICS as readonly string[]).includes(value ?? '');
const isGate = (value: string): value is Gate => (GATES as readonly string[]).includes(value);
const isMeasure = (value: string): value is CampaignMeasure => (CAMPAIGN_MEASURES as readonly string[]).includes(value);
const isVerdict = (value: string | null): boolean => (SERVICE_VERDICTS as readonly string[]).includes(value ?? '');
const isParameterKind = (value: string | null): value is ParameterKind => (PARAMETER_KINDS as readonly string[]).includes(value ?? '');

export function mechanicRef(code: string): MechanicRef {
  return { code: isMechanic(code) ? code : null, raw: code };
}

/** Both bounds or neither: a half interval is not shown as an interval. Pure. */
function interval(estimate: number, low: number | null, high: number | null): Interval {
  return low === null || high === null ? { estimate, low: null, high: null } : { estimate, low, high };
}

/** A gate this build knows, else `null` (shown verbatim, and treated as not validated). Pure. */
export function gateOf(raw: string): Gate | null {
  return isGate(raw) ? raw : null;
}

/**
 * The codes a `promotion.mechanic_vocabulary` row names, comma-separated (bata-platform
 * `parse_vocabulary`), or `null` with no row in force — distinct from a row naming nothing. Pure.
 */
export function parseVocabulary(text: string | null | undefined): readonly string[] | null {
  if (text === null || text === undefined) return null;
  return text.split(',').map((code) => code.trim()).filter((code) => code !== '');
}

/**
 * The mechanics that may be simulated: the ceiling narrowed by the row, in the ceiling's order.
 * No row governs none (ADR 8011) — never the ceiling by default. Pure.
 */
export function governedMechanics(codes: readonly string[] | null): readonly CampaignMechanic[] {
  if (codes === null) return [];
  return CAMPAIGN_MECHANICS.filter((code) => codes.includes(code));
}

/**
 * The mechanics a campaign may simulate: the governed ones less the Zona sale markdown
 * ({@link MARKDOWN_MECHANICS}, ADR 6078). Pure.
 */
export function simulableMechanics(codes: readonly string[] | null): readonly CampaignMechanic[] {
  return governedMechanics(codes).filter((code) => !MARKDOWN_MECHANICS.includes(code));
}

// ── the response fit, as the pages read it ───────────────────────────────────

export interface ResponsePoint {
  readonly depthPct: number | null;
  readonly buy: number | null;
  readonly pay: number | null;
  readonly windows: number;
  /** The predictive interval is wider than the registry allows: no figure is offered. */
  readonly intervalRefused: boolean;
  /** Net uplift against the history's mix: estimate and the PREDICTIVE interval for a new window. */
  readonly net: Interval | null;
  readonly units: Interval | null;
  readonly postWindowNet: Interval | null;
  readonly discountRate: Interval | null;
  readonly grossMarginRate: Interval | null;
}

export interface Coverage {
  readonly share: number | null;
  readonly predicted: number;
  readonly covered: number;
}

export interface ResponseValidation {
  readonly gate: Gate | null;
  readonly gateRaw: string;
  readonly rollingOrigin: Coverage;
  readonly leaveOneOut: Coverage;
  readonly signAccuracy: number | null;
  readonly maeLog: number | null;
  readonly maeLogNaive: number | null;
}

/** One mechanic on one channel. */
export interface ResponseMechanic {
  readonly channel: string;
  readonly windowsUsed: number;
  readonly mechanic: MechanicRef;
  readonly registered: boolean;
  readonly parameterKind: ParameterKind | null;
  readonly windows: number;
  readonly depthSupport: { readonly min: number; readonly max: number } | null;
  readonly quantitySupport: readonly { readonly buy: number; readonly pay: number }[];
  readonly refusal: { readonly reason: string; readonly required: number | null; readonly observed: number | null } | null;
  readonly validation: ResponseValidation | null;
  readonly points: readonly ResponsePoint[];
  /** In-scope cannibalisation (ADR 6067), `null` until the article panel lands. */
  readonly cannibalisation: { readonly share: Interval; readonly windows: number } | null;
}

export interface CampaignResponse {
  readonly fitId: string;
  readonly market: string;
  readonly fittedAt: string;
  readonly asOf: string;
  readonly dataFrom: string;
  readonly dataTo: string;
  readonly stageOneBasis: string;
  readonly clusterBasis: string | null;
  readonly articleNetBasis: string | null;
  readonly comparison: string;
  readonly intervalLevel: number;
  readonly coverageBand: { readonly low: number; readonly high: number };
  /** What "the mix" is: each level's share of the days in the fit's data window. */
  readonly baseMix: readonly { readonly level: string; readonly mechanic: MechanicRef | null; readonly share: number }[];
  /** Every channel × mechanic, in the platform's channel order and the ceiling's mechanic order. */
  readonly mechanics: readonly ResponseMechanic[];
}

const rate = (shape: RateShape | null): Interval | null => (shape === null ? null : interval(shape.estimate, shape.low, shape.high));
const uplift = (shape: UpliftShape | null): Interval | null => (shape === null ? null : interval(shape.estimate, shape.low, shape.high));

const mechanicOrder = (mechanic: MechanicRef): number => {
  const index = mechanic.code === null ? -1 : CAMPAIGN_MECHANICS.indexOf(mechanic.code);
  return index === -1 ? CAMPAIGN_MECHANICS.length : index;
};

/** The response read as the pages read it. Pure. */
export function campaignResponseOf(shape: CampaignResponseShape): CampaignResponse {
  const mechanics = shape.channels.flatMap((channel) => channel.mechanics
    .map((row): ResponseMechanic => ({
      channel: channel.channelKey,
      windowsUsed: channel.windowsUsed,
      mechanic: mechanicRef(row.mechanicCode),
      registered: row.mechanicRegistered,
      parameterKind: isParameterKind(row.parameterKind) ? row.parameterKind : null,
      windows: row.windowCount,
      depthSupport: row.depthSupport === null ? null : { min: row.depthSupport.min, max: row.depthSupport.max },
      quantitySupport: (row.quantitySupport ?? []).map((pair) => ({ buy: pair.buy, pay: pair.pay })),
      refusal: row.refusal === null ? null : { reason: row.refusal.reason, required: row.refusal.required, observed: row.refusal.observed },
      validation: row.validation === null ? null : {
        gate: gateOf(row.validation.gate),
        gateRaw: row.validation.gate,
        rollingOrigin: { share: row.validation.rollingOrigin.coverage, predicted: row.validation.rollingOrigin.predicted, covered: row.validation.rollingOrigin.covered },
        leaveOneOut: { share: row.validation.leaveOneOut.coverage, predicted: row.validation.leaveOneOut.predicted, covered: row.validation.leaveOneOut.covered },
        signAccuracy: row.validation.signAccuracy,
        maeLog: row.validation.maeLog,
        maeLogNaive: row.validation.maeLogNaive,
      },
      points: row.points.map((point) => ({
        depthPct: point.depthPct,
        buy: point.buyQuantity,
        pay: point.payQuantity,
        windows: point.windowsAtPoint,
        intervalRefused: point.intervalRefused,
        net: uplift(point.net),
        units: uplift(point.units),
        postWindowNet: uplift(point.postWindowNet),
        discountRate: rate(point.discountRate),
        grossMarginRate: rate(point.grossMarginRate),
      })),
      cannibalisation: row.inScopeCannibalisation === null || row.inScopeCannibalisation === undefined ? null : {
        share: interval(row.inScopeCannibalisation.ratio, row.inScopeCannibalisation.low, row.inScopeCannibalisation.high),
        windows: row.inScopeCannibalisation.windowCount,
      },
    }))
    .sort((a, b) => mechanicOrder(a.mechanic) - mechanicOrder(b.mechanic)));
  return {
    fitId: shape.fitId,
    market: shape.countryKey,
    fittedAt: shape.fittedAt,
    asOf: shape.asOf,
    dataFrom: shape.dataFrom,
    dataTo: shape.dataTo,
    stageOneBasis: shape.stageOneBasis,
    clusterBasis: shape.clusterBasis,
    articleNetBasis: shape.articleNetBasis ?? null,
    comparison: shape.comparison,
    intervalLevel: shape.intervalLevel,
    coverageBand: { low: shape.coverageBand.low, high: shape.coverageBand.high },
    baseMix: shape.baseMix.map((level) => ({
      level: level.levelKey,
      mechanic: level.mechanicCode === null ? null : mechanicRef(level.mechanicCode),
      share: level.share,
    })),
    mechanics,
  };
}

/** A fit's mechanic on the channels a campaign runs in (`BOTH` is both store channels). Pure. */
export function responseFor(fit: CampaignResponse, mechanic: CampaignMechanic, channel: CampaignChannel): readonly ResponseMechanic[] {
  const channels: readonly string[] = channel === 'BOTH' ? STORE_CHANNELS : [channel];
  return fit.mechanics.filter((row) => row.mechanic.code === mechanic && channels.includes(row.channel));
}

/** The parameter a mechanic takes: the fit's word when it published one, else the ceiling's. Pure. */
export function parameterOf(mechanic: CampaignMechanic, fit: CampaignResponse | null): ParameterKind {
  const said = fit?.mechanics.find((row) => row.mechanic.code === mechanic && row.parameterKind !== null)?.parameterKind;
  return said ?? MECHANIC_PARAMETER[mechanic];
}

// ── the campaign simulation, as the pages read it ────────────────────────────

export type StoresScope =
  | { readonly kind: 'all' }
  | { readonly kind: 'clusters'; readonly clusters: readonly string[] }
  | { readonly kind: 'districts'; readonly districts: readonly string[] }
  | { readonly kind: 'doors'; readonly doors: readonly string[] };

export interface ScopeRow {
  readonly kind: ArticleScopeKind | null;
  readonly kindRaw: string;
  readonly value: string;
}

export interface CampaignAlternative {
  readonly index: number;
  readonly label: string;
  readonly mechanic: MechanicRef;
  readonly depthPct: number | null;
  readonly buy: number | null;
  readonly pay: number | null;
}

export interface CampaignSimulation {
  readonly id: string;
  readonly baseRunId: string;
  readonly market: string;
  readonly scenarioId: string;
  readonly jobRunId: string;
  readonly fitId: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly channel: string;
  readonly banner: string;
  readonly stores: StoresScope;
  readonly include: readonly ScopeRow[];
  readonly exclude: readonly ScopeRow[];
  readonly alternatives: readonly CampaignAlternative[];
  readonly comparator: number;
  readonly requestedBy: string;
  /** The requester's profile name (bata-platform ADR 6198, DEC-111); `null` for the platform or a person with no name. */
  readonly requestedByName: string | null;
  /** Why no name stands (ADR 2127): `machine` | `service_account` | `no_profile`; `null` where one does. */
  readonly requestedByNameAbsentReason: string | null;
  readonly requestedAt: string;
  readonly state: string;
  readonly failureCode: string | null;
}

const strings = (value: unknown): readonly string[] => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []);

/** The doors as authored. A shape this build cannot read is "every door", as the request defaults. Pure. */
export function storesOf(stores: Readonly<Record<string, unknown>>): StoresScope {
  const kind = stores['kind'];
  if (kind === 'clusters') return { kind, clusters: strings(stores['clusterKeys']) };
  if (kind === 'districts') return { kind, districts: strings(stores['districtKeys']) };
  if (kind === 'doors') return { kind, doors: strings(stores['storeKeys']) };
  return { kind: 'all' };
}

function scopeRows(value: unknown): readonly ScopeRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((row: unknown): ScopeRow[] => {
    if (typeof row !== 'object' || row === null) return [];
    const record = row as Readonly<Record<string, unknown>>;
    const kind = typeof record['kind'] === 'string' ? record['kind'] : '';
    const text = typeof record['value'] === 'string' ? record['value'] : '';
    const known = (SCOPE_CLASSES as readonly string[]).includes(kind) || kind === ARTICLE_SCOPE_CLASS;
    return [{ kind: known ? (kind as ArticleScopeKind) : null, kindRaw: kind, value: text }];
  });
}

const alternativeOf = (row: CampaignAlternativeShape): CampaignAlternative => ({
  index: row.index,
  label: row.label,
  mechanic: mechanicRef(row.mechanicCode),
  depthPct: row.depthPct,
  buy: row.buyQuantity,
  pay: row.payQuantity,
});

/** One campaign simulation as the pages read it. Pure. */
export function campaignSimulationOf(shape: CampaignSimulationShape): CampaignSimulation {
  return {
    id: shape.simulationId,
    baseRunId: shape.baseRunId,
    market: shape.countryKey,
    scenarioId: shape.scenarioId,
    jobRunId: shape.jobRunId,
    fitId: shape.fitId,
    startsOn: shape.startsOn,
    endsOn: shape.endsOn,
    channel: shape.channel,
    banner: shape.banner,
    stores: storesOf(shape.stores),
    include: scopeRows(shape.articleScope['include']),
    exclude: scopeRows(shape.articleScope['exclude']),
    alternatives: shape.alternatives.map(alternativeOf).sort((a, b) => a.index - b.index),
    comparator: shape.comparator,
    requestedBy: shape.requestedBy,
    requestedByName: shape.requestedByName ?? null,
    requestedByNameAbsentReason: shape.requestedByNameAbsentReason ?? null,
    requestedAt: shape.requestedAt,
    state: shape.state,
    failureCode: shape.failureCode,
  };
}

/**
 * A page of a base run's campaign simulations, newest first, whether more exist, and the cursor to
 * them. An absent `has_more` (the wire's default) is no more; `next` is `null` unless more exist
 * AND the platform gave the cursor to them — a page that cannot be asked for is not fetched. Pure.
 */
export function campaignSimulationsOf(page: CursorPageShape<CampaignSimulationShape>): { readonly items: readonly CampaignSimulation[]; readonly more: boolean; readonly next: string | null } {
  const next = page.has_more === true ? (page.next_cursor ?? null) : null;
  return { items: page.items.map(campaignSimulationOf), more: page.has_more === true, next };
}

/** One figure: the median of the draws and its 80% interval, or `null` with why. */
export interface MeasureLine {
  readonly measure: CampaignMeasure | null;
  readonly measureRaw: string;
  readonly channel: string;
  /** `null`: absent — `absentReason` says why. Never a zero. */
  readonly value: Interval | null;
  readonly currency: string | null;
  readonly absentReason: string | null;
}

export interface AlternativeResult extends CampaignAlternative {
  readonly refusal: { readonly reason: string; readonly channel: string | null; readonly required: number | null; readonly observed: number | null } | null;
  readonly gate: Gate | null;
  readonly gateRaw: string;
  readonly notYetValidated: boolean;
  readonly measures: readonly MeasureLine[];
  readonly subCategories: readonly { readonly subCategory: string; readonly measures: readonly MeasureLine[] }[];
  /** Pass or breach against the service target; `null` not evaluated (or a word this build does not know). Never ranked. */
  readonly serviceVerdict: ServiceVerdict | null;
  readonly serviceVerdictRaw: string | null;
}

export interface CampaignDifference {
  readonly alternative: number;
  readonly comparator: number;
  readonly probabilityHigherRealisedMargin: number | null;
  /** Each answered alternative less the comparator, draw by draw: `units` is incremental units. */
  readonly measures: readonly MeasureLine[];
}

export interface CampaignResults {
  readonly simulation: CampaignSimulation;
  /** The whole run's refusal code, or `null` when it answered. */
  readonly refusal: string | null;
  readonly draws: number;
  readonly basis: {
    readonly fitId: string;
    readonly comparison: string;
    readonly forecastModelVersionId: string | null;
    readonly forecastRunId: string | null;
    readonly forecastStationary: boolean | null;
    readonly priceBasis: string | null;
    readonly priceAbsentReason: string | null;
    readonly objectiveConfigId: string | null;
    readonly objectiveAbsentReason: string | null;
    readonly serviceTarget: number | null;
    readonly serviceAbsentReason: string | null;
    readonly stockCells: number | null;
    /** Positions with no reported on-hand: left out of the test, never read as zero. */
    readonly unmeasuredCells: number | null;
    readonly priceRunId: string | null;
    readonly markdownAbsentReason: string | null;
  };
  readonly scope: {
    readonly articles: number;
    readonly doors: number;
    readonly byChannel: readonly { readonly channel: string; readonly doors: number; readonly articles: number; readonly baseUnits: number }[];
    /** What the lifecycle gate kept out (ADR 6078); `null` when it never judged — never "none". */
    readonly phaseExclusion: PhaseExclusion | null;
  };
  readonly comparator: number;
  /** In the order they were authored — never the platform's rank. */
  readonly alternatives: readonly AlternativeResult[];
  readonly differences: readonly CampaignDifference[];
}

/** Articles kept out of a campaign for one reason, as stated by one source (ADR 6078). */
export interface PhaseExclusionCount {
  readonly reason: string;
  readonly source: string;
  readonly articles: number;
}

/** What the lifecycle gate kept out of the perimeter, and the inferred batch it read. */
export interface PhaseExclusion {
  readonly articles: number;
  readonly inferredBatchId: string | null;
  readonly byReason: readonly PhaseExclusionCount[];
  /** Kept as main line with no phase stated (owner ruling 2026-10-10): not in `articles`. */
  readonly includedWithoutPhase: number;
}

const exclusionCount = (row: { readonly reason: string; readonly source: string; readonly articles: number }): PhaseExclusionCount => ({
  reason: row.reason, source: row.source, articles: row.articles,
});

function measureLine(row: CampaignMeasureShape): MeasureLine {
  return {
    measure: isMeasure(row.measure) ? row.measure : null,
    measureRaw: row.measure,
    channel: row.channelKey,
    value: row.estimate === null ? null : interval(row.estimate, row.low, row.high),
    currency: row.currencyCode,
    absentReason: row.estimate === null ? row.absentReason : null,
  };
}

/** A results read as the pages read it. Pure. */
export function campaignResultsOf(shape: CampaignResultsShape): CampaignResults {
  return {
    simulation: campaignSimulationOf(shape.simulation),
    refusal: shape.refusal ?? null,
    draws: shape.draws,
    basis: {
      fitId: shape.basis.fitId,
      comparison: shape.basis.comparison,
      forecastModelVersionId: shape.basis.forecastModelVersionId,
      forecastRunId: shape.basis.forecastRunId,
      forecastStationary: shape.basis.forecastStationary,
      priceBasis: shape.basis.priceBasis,
      priceAbsentReason: shape.basis.priceAbsentReason,
      objectiveConfigId: shape.basis.objectiveConfigId,
      objectiveAbsentReason: shape.basis.objectiveAbsentReason,
      serviceTarget: shape.basis.serviceTarget ?? null,
      serviceAbsentReason: shape.basis.serviceAbsentReason ?? null,
      stockCells: shape.basis.stockCells ?? null,
      unmeasuredCells: shape.basis.unmeasuredCells ?? null,
      priceRunId: shape.basis.priceRunId ?? null,
      markdownAbsentReason: shape.basis.markdownAbsentReason ?? null,
    },
    scope: {
      articles: shape.scope.articles,
      doors: shape.scope.doors,
      byChannel: shape.scope.byChannel.map((row) => ({ channel: row.channelKey, doors: row.doors, articles: row.articles, baseUnits: row.baseUnits })),
      phaseExclusion: shape.scope.phaseExclusion === undefined || shape.scope.phaseExclusion === null ? null : {
        articles: shape.scope.phaseExclusion.articles,
        inferredBatchId: shape.scope.phaseExclusion.inferredBatchId,
        byReason: shape.scope.phaseExclusion.byReason.map(exclusionCount),
        includedWithoutPhase: shape.scope.phaseExclusion.includedWithoutPhase ?? 0,
      },
    },
    comparator: shape.comparator,
    alternatives: shape.alternatives
      .map((row): AlternativeResult => ({
        ...alternativeOf(row),
        refusal: row.refusal === null ? null : { reason: row.refusal.reason, channel: row.refusal.channelKey, required: row.refusal.required, observed: row.refusal.observed },
        gate: gateOf(row.validation.gate),
        gateRaw: row.validation.gate,
        notYetValidated: row.validation.notYetValidated,
        measures: row.measures.map(measureLine),
        subCategories: (row.subCategories ?? []).map((group) => ({ subCategory: group.subCategory, measures: group.measures.map(measureLine) })),
        serviceVerdict: isVerdict(row.serviceVerdict ?? null) ? (row.serviceVerdict as ServiceVerdict) : null,
        serviceVerdictRaw: row.serviceVerdict ?? null,
      }))
      .sort((a, b) => a.index - b.index),
    differences: shape.differences.map((row) => ({
      alternative: row.alternative,
      comparator: row.comparator,
      probabilityHigherRealisedMargin: row.probabilityHigherRealisedMargin,
      measures: row.measures.map(measureLine),
    })),
  };
}

/** The line for one measure on one channel (`ALL` is every channel in scope), or `null`. Pure. */
export function measureOf(lines: readonly MeasureLine[], measure: CampaignMeasure, channel = 'ALL'): MeasureLine | null {
  return lines.find((line) => line.measure === measure && line.channel === channel) ?? null;
}

/**
 * The money state of an answer. `answered`: money was priced from the article panel. `absent`:
 * money was not priced — every money measure is `null` with the reason, units and pull-forward
 * units are still answered, and nothing is compared on margin (the phase 2 as-built note). Pure.
 */
export function moneyStateOf(results: CampaignResults): { readonly kind: 'answered' } | { readonly kind: 'absent'; readonly reason: string | null } {
  return results.basis.priceBasis !== null ? { kind: 'answered' } : { kind: 'absent', reason: results.basis.priceAbsentReason };
}

// ── Compose: the scope builder's two reads, and the request ──────────────────

export interface ScopeOptions {
  readonly channels: readonly { readonly channel: string; readonly doors: number }[];
  readonly clusters: readonly { readonly cluster: string; readonly doors: number }[];
  /** The store master's commercial districts, in number order (ADR 6076); empty until it states them. */
  readonly districts: readonly { readonly district: string; readonly doors: number }[];
  /** `codes`: the source codes behind the label (ADR 6076), sorted; empty until the master carries them. */
  readonly categories: readonly { readonly value: string; readonly articles: number; readonly codes: readonly string[] }[];
  readonly subCategories: readonly { readonly value: string; readonly category: string | null; readonly articles: number; readonly codes: readonly string[] }[];
  readonly banners: readonly string[];
}

export function scopeOptionsOf(shape: ScopeOptionsShape): ScopeOptions {
  return {
    channels: shape.channels.map((row) => ({ channel: row.channelKey, doors: row.doors })),
    clusters: shape.clusters.map((row) => ({ cluster: row.clusterKey, doors: row.doors })),
    districts: (shape.districts ?? []).map((row) => ({ district: row.districtKey, doors: row.doors })),
    categories: shape.categories.map((row) => ({ value: row.value, articles: row.articles, codes: [...(row.codes ?? [])] })),
    subCategories: shape.subCategories.map((row) => ({ value: row.value, category: row.category, articles: row.articles, codes: [...(row.codes ?? [])] })),
    banners: [...shape.banners],
  };
}

/** One typed article the master carries: its name (may be absent), labels and codes. */
export interface KnownArticle {
  readonly article: string;
  readonly description: string | null;
  readonly category: string | null;
  readonly subCategory: string | null;
  readonly categoryCode: string | null;
  readonly subCategoryCode: string | null;
  /** Why the lifecycle gate keeps it out of a campaign over the window (ADR 6078), else `null`. */
  readonly excludedReason: string | null;
  /** No phase stated for the window: kept as main line, and said (owner ruling 2026-10-10). */
  readonly includedWithoutPhase: boolean;
}

/** A typed list checked: the known, and the unknown named in the order typed. */
export interface ArticleLookup {
  readonly known: readonly KnownArticle[];
  readonly unknown: readonly string[];
  /** `false` when the market has no inferred-phase batch: no article's phase is known (ADR 6078). */
  readonly phaseFeedLanded: boolean;
}

/** One or more lookup reads, merged in the order they were asked. Pure. */
export function articleLookupOf(shapes: readonly ScopeArticlesShape[]): ArticleLookup {
  return {
    known: shapes.flatMap((shape) => shape.known.map((row) => ({
      article: row.articleKey,
      description: row.description,
      category: row.category,
      subCategory: row.subCategory,
      categoryCode: row.categoryCode,
      subCategoryCode: row.subCategoryCode,
      excludedReason: row.excludedReason ?? null,
      includedWithoutPhase: row.includedWithoutPhase ?? false,
    }))),
    unknown: shapes.flatMap((shape) => [...shape.unknown]),
    phaseFeedLanded: shapes.every((shape) => shape.phaseFeedLanded ?? true),
  };
}

/** The listed articles the request refuses: in the Zona sale or the outlet for the window. Pure. */
export function markdownPhaseArticles(lookup: ArticleLookup): readonly KnownArticle[] {
  return lookup.known.filter((row) => row.excludedReason !== null && (MARKDOWN_PHASE_REASONS as readonly string[]).includes(row.excludedReason));
}

export interface ScopeCount {
  /** The articles the lifecycle gate keeps (ADR 6078); before the gate when no phase batch has landed. */
  readonly articles: number;
  readonly doors: number;
  readonly byChannel: readonly { readonly channel: string; readonly doors: number; readonly articles: number }[];
  /** What the gate keeps out, by reason and source. */
  readonly excluded: readonly PhaseExclusionCount[];
  /** `false`: the market has no inferred-phase batch, and a simulation would refuse. */
  readonly phaseFeedLanded: boolean;
  /** Kept as main line with no phase stated (owner ruling 2026-10-10), counted in `articles`. */
  readonly includedWithoutPhase: number;
}

export function scopeCountOf(shape: ScopeCountShape): ScopeCount {
  return {
    articles: shape.articles,
    doors: shape.doors,
    byChannel: shape.byChannel.map((row) => ({ channel: row.channelKey, doors: row.doors, articles: row.articles })),
    excluded: (shape.excluded ?? []).map(exclusionCount),
    phaseFeedLanded: shape.phaseFeedLanded ?? true,
    includedWithoutPhase: shape.includedWithoutPhase ?? 0,
  };
}

/** One alternative as Compose holds it, complete. */
export interface ComposedAlternative {
  readonly label: string;
  readonly mechanic: CampaignMechanic;
  readonly depthPct: number | null;
  readonly buy: number | null;
  readonly pay: number | null;
}

/**
 * One include or exclude row, complete. `category` names a `SUB_CATEGORY` row's category, as the
 * options read gave it (ADR 6071); absent or `null` on a `CATEGORY` row and on a sub-category the
 * panel's labels place under no category.
 */
export interface ComposedScopeRow {
  readonly kind: ArticleScopeKind;
  readonly value: string;
  readonly category?: string | null;
}

/** The separator of `CATEGORY::SUB_CATEGORY`, the qualified sub-category `/scope` accepts (ADR 6071). */
export const SUB_CATEGORY_QUALIFIER = '::';

/**
 * A sub-category as `/scope` reads it: `CATEGORY::SUB_CATEGORY` when its category is known, the
 * plain name otherwise (which the platform qualifies when it sits under one category only). Pure.
 */
export function qualifiedSubCategory(row: { readonly value: string; readonly category?: string | null }): string {
  return row.category === undefined || row.category === null || row.category === ''
    ? row.value
    : `${row.category}${SUB_CATEGORY_QUALIFIER}${row.value}`;
}

/** A scope row on the request body: `category` only on a sub-category whose category is known. Pure. */
function scopeRowShape(row: ComposedScopeRow): ScopeRowShape {
  const category = row.kind === 'SUB_CATEGORY' && row.category !== undefined && row.category !== null && row.category !== '' ? row.category : null;
  return category === null ? { kind: row.kind, value: row.value } : { kind: row.kind, value: row.value, category };
}

/** A campaign question as Compose holds it, complete. */
export interface ComposedCampaign {
  readonly baseRunId: string;
  readonly scenarioId: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly channel: CampaignChannel;
  readonly stores: StoresScope;
  readonly include: readonly ComposedScopeRow[];
  readonly exclude: readonly ComposedScopeRow[];
  readonly alternatives: readonly ComposedAlternative[];
  readonly comparator: number;
}

function storesShape(stores: StoresScope): StoresShape {
  if (stores.kind === 'clusters') return { kind: 'clusters', clusterKeys: stores.clusters };
  if (stores.kind === 'districts') return { kind: 'districts', districtKeys: stores.districts };
  if (stores.kind === 'doors') return { kind: 'doors', storeKeys: stores.doors };
  return { kind: 'all' };
}

/** The request body. The banner is always `BOTH` — the only one the platform can answer. Pure. */
export function campaignRequestOf(question: ComposedCampaign): CampaignRequestParams {
  return {
    body: {
      baseRunId: question.baseRunId,
      scenarioId: question.scenarioId,
      startsOn: question.startsOn,
      endsOn: question.endsOn,
      channel: question.channel,
      banner: ANSWERABLE_BANNER,
      stores: storesShape(question.stores),
      articleScope: {
        include: question.include.map(scopeRowShape),
        exclude: question.exclude.map(scopeRowShape),
      },
      alternatives: question.alternatives.map((row) => ({
        label: row.label,
        mechanicCode: row.mechanic,
        depthPct: row.depthPct,
        buyQuantity: row.buy,
        payQuantity: row.pay,
      })),
      comparator: question.comparator,
    },
  };
}

/** The resolved-count read for a scope: nothing is written. Pure. */
export function scopeCountParams(
  question: Pick<ComposedCampaign, 'baseRunId' | 'startsOn' | 'channel' | 'stores' | 'include' | 'exclude'> & { readonly endsOn?: string | null },
): CampaignScopeCountParams {
  const values = (rows: readonly ComposedScopeRow[], kind: ArticleScopeKind): readonly string[] => rows
    .filter((row) => row.kind === kind)
    .map((row) => (kind === 'SUB_CATEGORY' ? qualifiedSubCategory(row) : row.value));
  const lists = {
    cluster_key: question.stores.kind === 'clusters' ? question.stores.clusters : [],
    district_key: question.stores.kind === 'districts' ? question.stores.districts : [],
    store_key: question.stores.kind === 'doors' ? question.stores.doors : [],
    include_category: values(question.include, 'CATEGORY'),
    include_sub_category: values(question.include, 'SUB_CATEGORY'),
    exclude_category: values(question.exclude, 'CATEGORY'),
    exclude_sub_category: values(question.exclude, 'SUB_CATEGORY'),
    include_article: values(question.include, ARTICLE_SCOPE_CLASS),
    exclude_article: values(question.exclude, ARTICLE_SCOPE_CLASS),
  };
  const query: Record<string, unknown> = {
    run_id: question.baseRunId,
    starts_on: question.startsOn,
    channel: question.channel,
    stores_kind: question.stores.kind,
  };
  if (question.endsOn !== undefined && question.endsOn !== null && question.endsOn !== '') query['ends_on'] = question.endsOn;
  for (const [name, list] of Object.entries(lists)) if (list.length > 0) query[name] = list;
  return { query: query as CampaignScopeCountParams['query'] };
}

/** The members a request refusal names (camelCase on the wire), read off the problem document. */
export interface RequestRefusalMembers {
  readonly index: number | null;
  readonly mechanicCode: string | null;
  readonly reason: string | null;
  readonly maxDays: number | null;
  readonly kind: string | null;
  readonly banner: string | null;
  readonly keys: readonly string[];
  /** `article_in_markdown_phase`: each key's reason, in the same order (ADR 6078). */
  readonly phases: readonly string[];
}

export function requestRefusalMembers(problem: Readonly<Record<string, unknown>>): RequestRefusalMembers {
  const number = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
  const text = (value: unknown): string | null => (typeof value === 'string' ? value : null);
  return {
    index: number(problem['index']),
    mechanicCode: text(problem['mechanicCode']),
    reason: text(problem['reason']),
    maxDays: number(problem['maxDays']),
    kind: text(problem['kind']),
    banner: text(problem['banner']),
    keys: strings(problem['keys']),
    phases: strings(problem['phases']),
  };
}
